import {
    cleanupVideoInput,
    formatFileSize,
    isSupportedVideoFile,
    loadFFmpeg,
    prepareVideoInput,
} from '../compress/compressVideoEngine';

export {
    formatFileSize,
    isSupportedVideoFile,
};

export const MAX_VOLUME_VIDEO_BYTES = 200 * 1024 * 1024;
export const MIN_VOLUME_PERCENT = 0;
export const MAX_VOLUME_PERCENT = 300;
export const DEFAULT_VOLUME_PERCENT = 100;

export function volumeVideoName(fileName, { muted = false, volumePercent = 100 } = {}) {
    const base = (fileName || 'video').replace(/\.[^.]+$/, '');
    const suffix = muted ? 'muted' : `volume-${volumePercent}`;
    return `${base}-${suffix}.mp4`;
}

/**
 * @param {File} file
 * @param {number} volumePercent
 * @param {(progress: number) => void} onProgress
 * @param {{ removeAudio?: boolean }} options
 * @returns {Promise<Blob>}
 */
export async function adjustVideoVolume(
    file,
    volumePercent = DEFAULT_VOLUME_PERCENT,
    onProgress = () => {},
    options = {},
) {
    const { removeAudio = false } = options;
    let ffmpeg;
    let videoInput = null;
    const outputName = 'volume-output.mp4';

    try {
        ffmpeg = await loadFFmpeg();
        videoInput = await prepareVideoInput(ffmpeg, file);

        const percent = Number(volumePercent);
        const safePercent = Number.isFinite(percent) ? Math.max(0, percent) : DEFAULT_VOLUME_PERCENT;
        const multiplier = safePercent / 100;

        const progressHandler = ({ progress }) => {
            onProgress(Math.round(Math.min(progress * 100, 100)));
        };
        ffmpeg.on('progress', progressHandler);

        const ffmpegArgs = removeAudio
            ? [
                '-i', videoInput.inputPath,
                '-an',
                '-vcodec', 'copy',
                '-movflags', '+faststart',
                outputName,
            ]
            : [
                '-i', videoInput.inputPath,
                '-filter:a', `volume=${multiplier}`,
                '-vcodec', 'copy',
                '-acodec', 'aac',
                '-movflags', '+faststart',
                outputName,
            ];

        const exitCode = await ffmpeg.exec(ffmpegArgs);
        ffmpeg.off('progress', progressHandler);

        if (exitCode !== 0) {
            try { await ffmpeg.deleteFile(outputName); } catch (_) { /* ignore */ }
            throw new Error('ENCODE_FAILED');
        }

        const data = await ffmpeg.readFile(outputName);
        try { await ffmpeg.deleteFile(outputName); } catch (_) { /* ignore */ }

        if (!data || data.length === 0) {
            throw new Error('EMPTY_OUTPUT');
        }

        return new Blob([data], { type: 'video/mp4' });
    } catch (error) {
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('memory') || msg.includes('allocate') || msg.includes('oom')) {
            throw new Error('OUT_OF_MEMORY');
        }
        if (error?.message === 'ENCODE_FAILED' || error?.message === 'EMPTY_OUTPUT') {
            throw error;
        }
        throw new Error('VOLUME_FAILED');
    } finally {
        if (ffmpeg && videoInput) {
            await cleanupVideoInput(ffmpeg, videoInput);
        }
    }
}
